import type { AbilityDef } from '../shared/abilities';
import { TAU } from '../shared/math';
import type { ItemId } from '../shared/types';

/**
 * Item artwork, drawn with canvas paths so the game still ships no image files.
 *
 * Every icon is drawn inside a 32x32 box centred on the origin; `size` scales
 * that box to whatever the caller needs, which lets the very same drawing serve
 * a 26px quick-bar slot, a 34px shop row and a world drop.
 */
export type IconKind = ItemId | 'coin';

const GLASS_LIGHT = 'rgba(198, 224, 246, 0.34)';
const GLASS_EDGE = 'rgba(18, 26, 40, 0.8)';
const LIQUID_HILIGHT = 'rgba(255, 255, 255, 0.26)';
const SHINE = 'rgba(255, 255, 255, 0.55)';

export function drawItemIcon(
  ctx: CanvasRenderingContext2D,
  kind: IconKind,
  size: number,
  t = 0,
): void {
  const scale = size / 32;
  ctx.save();
  ctx.scale(scale, scale);
  ctx.lineJoin = 'round';
  switch (kind) {
    case 'health':
      drawRoundFlask(ctx, '#d94a41', '#ff8a7d', t);
      break;
    case 'mana':
      drawTeardropFlask(ctx, '#3f7ddb', '#8fc0ff', t);
      break;
    case 'scroll':
      drawScroll(ctx);
      break;
    case 'coin':
      drawCoin(ctx, t);
      break;
  }
  ctx.restore();
}

/** Soft coloured glow behind the glass, so potions pop against dark panels. */
function liquidGlow(ctx: CanvasRenderingContext2D, colour: string, y: number, r: number): void {
  const gradient = ctx.createRadialGradient(0, y, 0, 0, y, r);
  gradient.addColorStop(0, colour);
  gradient.addColorStop(0.55, colour.replace('rgb', 'rgba').replace(')', ', 0.35)'));
  gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(0, y, r, 0, TAU);
  ctx.fill();
}

function drawCork(ctx: CanvasRenderingContext2D, width: number, y: number): void {
  ctx.beginPath();
  ctx.roundRect(-width / 2, y, width, 6, 2.4);
  ctx.fillStyle = '#c08b4e';
  ctx.fill();
  ctx.strokeStyle = '#7d5730';
  ctx.lineWidth = 1.3;
  ctx.stroke();
  ctx.fillStyle = 'rgba(255, 236, 200, 0.35)';
  ctx.fillRect(-width / 2 + 1.2, y + 1.2, width - 2.4, 1.5);
}

/** Squat bulb flask with a short neck - the health potion. */
function drawRoundFlask(
  ctx: CanvasRenderingContext2D,
  colour: string,
  glow: string,
  t: number,
): void {
  ctx.save();
  liquidGlow(ctx, hexToRgbString(glow), 3, 17);
  ctx.restore();

  // Neck
  ctx.beginPath();
  ctx.roundRect(-3.6, -9.5, 7.2, 9, 2);
  ctx.fillStyle = GLASS_LIGHT;
  ctx.fill();
  ctx.strokeStyle = GLASS_EDGE;
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Bulb
  ctx.beginPath();
  ctx.arc(0, 3.5, 10.6, 0, TAU);
  ctx.fillStyle = GLASS_LIGHT;
  ctx.fill();

  // Liquid
  ctx.save();
  ctx.beginPath();
  ctx.arc(0, 3.5, 9.6, 0, TAU);
  ctx.clip();
  ctx.fillStyle = colour;
  ctx.fillRect(-11, 2.4, 22, 12);
  ctx.fillStyle = LIQUID_HILIGHT;
  ctx.fillRect(-11, 2.4, 22, 2);
  // A couple of rising bubbles.
  ctx.fillStyle = 'rgba(255, 255, 255, 0.35)';
  for (let i = 0; i < 2; i++) {
    const phase = ((t * 0.6 + i * 0.5) % 1);
    const by = 12 - phase * 9;
    ctx.beginPath();
    ctx.arc(-3 + i * 6, by, 1.5 - phase * 0.5, 0, TAU);
    ctx.fill();
  }
  ctx.restore();

  ctx.beginPath();
  ctx.arc(0, 3.5, 10.6, 0, TAU);
  ctx.strokeStyle = GLASS_EDGE;
  ctx.lineWidth = 1.6;
  ctx.stroke();

  // Glass shine
  ctx.beginPath();
  ctx.ellipse(-4.6, 0.2, 2.5, 4.5, -0.35, 0, TAU);
  ctx.fillStyle = SHINE;
  ctx.fill();

  drawCork(ctx, 9, -14.5);
}

/** Tall teardrop bottle - the mana potion. */
function drawTeardropFlask(
  ctx: CanvasRenderingContext2D,
  colour: string,
  glow: string,
  t: number,
): void {
  ctx.save();
  liquidGlow(ctx, hexToRgbString(glow), 3, 17);
  ctx.restore();

  const body = (): void => {
    ctx.beginPath();
    ctx.moveTo(-3.6, -10);
    ctx.lineTo(3.6, -10);
    ctx.quadraticCurveTo(11.4, 2, 0, 13.2);
    ctx.quadraticCurveTo(-11.4, 2, -3.6, -10);
    ctx.closePath();
  };

  body();
  ctx.fillStyle = GLASS_LIGHT;
  ctx.fill();

  ctx.save();
  body();
  ctx.clip();
  ctx.fillStyle = colour;
  ctx.fillRect(-12, 1.2, 24, 16);
  ctx.fillStyle = LIQUID_HILIGHT;
  ctx.fillRect(-12, 1.2, 24, 2);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
  for (let i = 0; i < 3; i++) {
    const phase = ((t * 0.7 + i * 0.34) % 1);
    ctx.beginPath();
    ctx.arc(-4 + i * 4.5, 11 - phase * 12, 1.5 - phase * 0.6, 0, TAU);
    ctx.fill();
  }
  ctx.restore();

  body();
  ctx.strokeStyle = GLASS_EDGE;
  ctx.lineWidth = 1.6;
  ctx.stroke();

  ctx.beginPath();
  ctx.ellipse(-4, -1.5, 2.1, 4, -0.3, 0, TAU);
  ctx.fillStyle = SHINE;
  ctx.fill();

  drawCork(ctx, 9.4, -15);
}

function drawScroll(ctx: CanvasRenderingContext2D): void {
  ctx.save();
  ctx.shadowColor = 'rgba(233, 200, 120, 0.55)';
  ctx.shadowBlur = 9;
  ctx.beginPath();
  ctx.roundRect(-11, -8, 22, 16, 3);
  ctx.fillStyle = '#e9dcb4';
  ctx.fill();
  ctx.restore();

  // Aged shading at the top and bottom
  ctx.fillStyle = 'rgba(160, 138, 92, 0.28)';
  ctx.fillRect(-11, -8, 22, 2.4);
  ctx.fillRect(-11, 5.6, 22, 2.4);

  // Faint writing
  ctx.strokeStyle = 'rgba(120, 100, 62, 0.5)';
  ctx.lineWidth = 1.1;
  for (let i = 0; i < 3; i++) {
    ctx.beginPath();
    ctx.moveTo(-6.5, -3.4 + i * 3.4);
    ctx.lineTo(6.5, -3.4 + i * 3.4);
    ctx.stroke();
  }

  // Rolled ends
  for (const side of [-1, 1]) {
    ctx.beginPath();
    ctx.ellipse(side * 11, 0, 3.4, 8.2, 0, 0, TAU);
    ctx.fillStyle = '#cdbb90';
    ctx.fill();
    ctx.strokeStyle = '#9c8a63';
    ctx.lineWidth = 1.3;
    ctx.stroke();
  }

  // Ribbon
  ctx.fillStyle = '#b03a3a';
  ctx.fillRect(-11, -2.2, 22, 4.4);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.18)';
  ctx.fillRect(-11, 0.6, 22, 1.4);
}

function drawCoin(ctx: CanvasRenderingContext2D, t: number): void {
  const wobble = Math.sin(t * 2) * 0.6;
  ctx.save();
  ctx.rotate(wobble * 0.04);

  ctx.beginPath();
  ctx.arc(0, 0, 10.5, 0, TAU);
  ctx.fillStyle = '#9a6d0c';
  ctx.fill();

  ctx.beginPath();
  ctx.arc(0, -0.6, 9.2, 0, TAU);
  ctx.fillStyle = '#f2c94c';
  ctx.fill();

  ctx.beginPath();
  ctx.arc(0, -0.6, 9.2, 0, TAU);
  ctx.strokeStyle = 'rgba(120, 84, 6, 0.55)';
  ctx.lineWidth = 1.2;
  ctx.stroke();

  // Stamped mark
  ctx.fillStyle = 'rgba(140, 98, 10, 0.55)';
  ctx.beginPath();
  ctx.moveTo(0, -5.2);
  ctx.lineTo(4.2, -1.4);
  ctx.lineTo(2.6, 4);
  ctx.lineTo(-2.6, 4);
  ctx.lineTo(-4.2, -1.4);
  ctx.closePath();
  ctx.fill();

  ctx.beginPath();
  ctx.ellipse(-3.4, -4, 2.6, 1.6, -0.5, 0, TAU);
  ctx.fillStyle = 'rgba(255, 246, 210, 0.85)';
  ctx.fill();

  ctx.restore();
}

export function drawIconCanvas(
  canvas: HTMLCanvasElement,
  kind: IconKind,
  size: number,
): void {
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  canvas.width = Math.round(size * dpr);
  canvas.height = Math.round(size * dpr);
  canvas.style.width = `${size}px`;
  canvas.style.height = `${size}px`;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, size, size);
  ctx.save();
  ctx.translate(size / 2, size / 2);
  drawItemIcon(ctx, kind, size);
  ctx.restore();
}

/* ---------------------------------------------------------------------- abilities */

/** Ability glyphs, drawn in the same 32x32 box as the item icons. */
export function drawAbilityIcon(
  ctx: CanvasRenderingContext2D,
  def: AbilityDef,
  size: number,
  t = 0,
): void {
  const scale = size / 32;
  ctx.save();
  ctx.scale(scale, scale);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = def.colour;
  ctx.fillStyle = def.colour;

  // Soft coloured halo so abilities pop against the dark HUD.
  const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, 16);
  gradient.addColorStop(0, 'rgba(255, 255, 255, 0.12)');
  gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(0, 0, 16, 0, TAU);
  ctx.fill();
  ctx.fillStyle = def.colour;

  switch (def.id) {
    case 'whirlwind':
      drawWhirlwindGlyph(ctx, t);
      break;
    case 'charge':
      drawChargeGlyph(ctx);
      break;
    case 'volley':
      drawVolleyGlyph(ctx);
      break;
    case 'pierce':
      drawPierceGlyph(ctx);
      break;
    case 'nova':
      drawNovaGlyph(ctx, t);
      break;
    case 'blink':
      drawBlinkGlyph(ctx, t);
      break;
  }
  ctx.restore();
}

function arrowHead(ctx: CanvasRenderingContext2D, x: number, y: number, angle: number, size = 5): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.beginPath();
  ctx.moveTo(size, 0);
  ctx.lineTo(-size * 0.7, -size * 0.75);
  ctx.lineTo(-size * 0.7, size * 0.75);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function drawWhirlwindGlyph(ctx: CanvasRenderingContext2D, t: number): void {
  ctx.lineWidth = 3.4;
  for (let i = 0; i < 2; i++) {
    ctx.save();
    ctx.rotate(t * 1.6 + i * Math.PI);
    ctx.beginPath();
    ctx.arc(0, 0, 9.5, 0.25, 2.5);
    ctx.stroke();
    arrowHead(ctx, Math.cos(2.5) * 9.5, Math.sin(2.5) * 9.5, 2.5 + Math.PI / 2, 4);
    ctx.restore();
  }
}

function drawChargeGlyph(ctx: CanvasRenderingContext2D): void {
  ctx.lineWidth = 3.6;
  for (let i = 0; i < 3; i++) {
    const x = -10 + i * 7;
    const alpha = 0.45 + i * 0.275;
    ctx.globalAlpha = alpha;
    ctx.beginPath();
    ctx.moveTo(x, -9);
    ctx.lineTo(x + 7, 0);
    ctx.lineTo(x, 9);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

function drawVolleyGlyph(ctx: CanvasRenderingContext2D): void {
  ctx.lineWidth = 2.8;
  for (let i = -1; i <= 1; i++) {
    const angle = -Math.PI / 2 + i * 0.45;
    const x = Math.cos(angle) * 13;
    const y = 7 + Math.sin(angle) * 13;
    ctx.beginPath();
    ctx.moveTo(0, 8);
    ctx.lineTo(x, y);
    ctx.stroke();
    arrowHead(ctx, x, y, angle, 4.2);
  }
  // Bow
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(0, 15, 11, Math.PI * 1.15, Math.PI * 1.85);
  ctx.stroke();
}

function drawPierceGlyph(ctx: CanvasRenderingContext2D): void {
  ctx.lineWidth = 3.4;
  ctx.beginPath();
  ctx.moveTo(-12, 0);
  ctx.lineTo(8, 0);
  ctx.stroke();
  arrowHead(ctx, 8, 0, 0, 5.6);

  ctx.globalAlpha = 0.5;
  ctx.lineWidth = 2.2;
  for (const y of [-6, 6]) {
    ctx.beginPath();
    ctx.moveTo(-12, y);
    ctx.lineTo(-3, y);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

function drawNovaGlyph(ctx: CanvasRenderingContext2D, t: number): void {
  const pulse = 0.85 + Math.sin(t * 2.4) * 0.15;
  ctx.save();
  ctx.rotate(t * 0.5);
  for (let i = 0; i < 8; i++) {
    ctx.save();
    ctx.rotate((i / 8) * TAU);
    ctx.beginPath();
    ctx.moveTo(0, -4.5 * pulse);
    ctx.lineTo(-2.6, -13 * pulse);
    ctx.lineTo(0, -11 * pulse);
    ctx.lineTo(2.6, -13 * pulse);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }
  ctx.restore();
  ctx.beginPath();
  ctx.arc(0, 0, 4.2, 0, TAU);
  ctx.fill();
}

function drawBlinkGlyph(ctx: CanvasRenderingContext2D, t: number): void {
  ctx.globalAlpha = 0.55;
  ctx.lineWidth = 2.4;
  ctx.setLineDash([3, 3]);
  ctx.lineDashOffset = -t * 6;
  ctx.beginPath();
  ctx.arc(0, 0, 9.5, 0, TAU);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.globalAlpha = 1;
  ctx.beginPath();
  ctx.arc(-4.5, 4.5, 3.4, 0, TAU);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(5, -5, 4.4, 0, TAU);
  ctx.fill();
}

export function drawAbilityCanvas(
  canvas: HTMLCanvasElement,
  def: AbilityDef,
  size: number,
): void {
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  canvas.width = Math.round(size * dpr);
  canvas.height = Math.round(size * dpr);
  canvas.style.width = `${size}px`;
  canvas.style.height = `${size}px`;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, size, size);
  ctx.save();
  ctx.translate(size / 2, size / 2);
  drawAbilityIcon(ctx, def, size, 0);
  ctx.restore();
}

/** `#rrggbb` -> `rgb(r, g, b)` so a radial gradient can fade it out. */function hexToRgbString(hex: string): string {
  const value = hex.replace('#', '');
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgb(${r}, ${g}, ${b})`;
}
